/* eslint-disable @typescript-eslint/no-misused-promises */
import { TranscriptionService } from '@klangio/ai2notes-ts-client';
import * as React from 'react';
import { useContext, useState } from 'react';

import { Modal, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { AppConstantsContext, UserContext } from '../../../../context';
import { TranscriptionDialogContext } from '../../../../context/TranscriptionDialogContext/TranscriptionDialogContext';
import {
  backendService,
  TrackingCategory,
  trackingService,
  TrackingTranscriptionParams,
} from '../../../../services';

import {
  hasCapo,
  hasGuitarStyle,
  hasTuning,
  hasVocals,
  isArrangement,
  isDrums,
  isPiano,
  isSolo,
  isWindInstrument,
  noInstrumentSelected,
  oneInstrumentSelected,
  shouldSpecifyInstrument,
  STEPS,
} from './TranscriptionDialog.config';

import { JobType } from '../../../../entities';
import type { ITheme } from '../../../../theme';
import { useDurationTracking } from './DurationTracking.hook';
import { StepFrame } from './StepFrame';
import AdvancedSettings from './steps/AdvancedSettings';
import Capo from './steps/Capo';
import ComposerTitle from './steps/ComposerTitle';
import Difficulty from './steps/Difficulty';
import DrumSet from './steps/DrumSet';
import GuitarStyle from './steps/GuitarStyle';
import InstrumentsSelection from './steps/InstrumentsSelection';
import Lyrics from './steps/Lyrics';
import PedalMarkings from './steps/PedalMarkings';
import PianoStyle from './steps/PianoStyle';
import SoloComponent from './steps/SoloComponent';
import SpecificInstrument from './steps/SpecificInstrument';
import TranscriptionMode from './steps/TranscriptionMode/TranscriptionMode';
import Tuning from './steps/Tuning';
import { ShowOnLastStep } from './Wizard/ShowOnLastStep';
import { Step, Wizard, WizardCurrent } from './Wizard/WizardContext';
import RockModeForGuitarSummit from './steps/RockModeForGuitarSummit';

export interface TranscriptionDialogProps {
  open: boolean;
  handleClose: () => void;
  handleProcessing?: (processing: number) => void;
  handleResolve?: (jobId: string, useTickets: boolean) => void;
  customSubmitButton?: React.ReactNode;
  isRetranscribe?: boolean;
  location?: (typeof TrackingTranscriptionParams.location)['songbook' | 'wp' | 'nav'];
}

const useStyles = makeStyles()((theme: ITheme) => ({
  modal: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  root: {
    position: 'relative',
    width: '90%',
    maxWidth: '700px',
    padding: '2em',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1em',
    boxShadow: `0px 0px 5px 0 ${theme.palette.primary.main}`,
    [theme.breakpoints.down('sm')]: {
      margin: '1em auto',
      padding: '1em',
    },
    maxHeight: '95vh', // Added to ensure modal doesn't exceed viewport height
  },
}));
const TranscriptionDialog: React.FC<TranscriptionDialogProps> = ({
  open,
  handleClose,
  handleProcessing,
  handleResolve,
  customSubmitButton,
  isRetranscribe,
  location,
}) => {
  // const { stepList, setStepList } = useSteps();

  const { jobType } = useContext(AppConstantsContext);
  const { settings, apriori, videoUrl, file, reset, source } = useContext(
    TranscriptionDialogContext
  );
  const { classes } = useStyles();
  const { user } = useContext(UserContext);
  const [showTranscriptionDialogClose, setShowTranscriptionDialogClose] = useState(false);
  const { getElapsedSeconds } = useDurationTracking(open);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = (useTickets: boolean) => {
    setSubmitting(true);
    let mode;
    if (!settings?.solo && !settings?.arrangement) {
      mode = 'multi';
    } else if (settings?.solo) {
      mode = 'solo';
    } else if (settings?.arrangement) {
      mode = 'arrangement';
    }
    const trackingValues = {
      type: useTickets
        ? TrackingTranscriptionParams.type.full
        : TrackingTranscriptionParams.type.free,
      location,
      lyrics: settings?.vocals?.lyrics,
      lang: settings?.vocals?.lang,
      instruments: settings?.instruments,
      amount: settings?.instruments?.length,
      difficulty: settings?.difficulty,
      model: settings?.model,
      mode,
      automatic: settings?.instruments?.length === 0,
      input: source,
      duration: getElapsedSeconds() ?? 0,
    };
    trackingService.valueObjectEvent(
      TrackingCategory.Transcription,
      TrackingTranscriptionParams.action.create,
      {
        ...trackingValues,
      }
    );
    const params = {
      settings,
      apriori,
    };

    if (videoUrl && !file) {
      return TranscriptionService.transcriptionSocial({
        url: videoUrl,
        useTickets,
        params: JSON.stringify(params),
        type: jobType,
      }).then((res) => {
        handleResolve?.(res?.jobId, useTickets);
      });
    }

    return backendService.transcribeFile(
      file,
      useTickets,
      params,
      source,
      jobType,
      user?.token,
      (perc) => handleProcessing?.(perc),
      (jobId) => handleResolve?.(jobId.toString(), useTickets)
    );
  };

  /** Close */
  const handleCloseDialog = (step: string) => {
    reset();
    setSubmitting(false);
    const trackingValues = {
      duration: getElapsedSeconds() ?? 0,
      currentStep: step,
      location,
    };
    trackingService.valueObjectEvent(
      TrackingCategory.Transcription,
      TrackingTranscriptionParams.action.close,
      trackingValues
    );
    handleClose();
  };

  return (
    <Modal
      open={open}
      className={classes.modal}
      onClose={() => setShowTranscriptionDialogClose(true)}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: '#000000d6',
          },
        },
      }}>
      <Paper className={classes.root}>
        <Wizard>
          {/* current step */}
          <WizardCurrent />
          {/* step list */}
          <Step
            id={STEPS.CHOOSE_INSTRUMENT_MODEL_GUITAR_SUMMIT}
            when={() => location === 'guitar-summit'}>
            <StepFrame
              showCloseDialog={showTranscriptionDialogClose}
              setShowCloseDialog={setShowTranscriptionDialogClose}
              submitting={submitting}
              handleClose={handleCloseDialog}
              id={STEPS.CHOOSE_INSTRUMENT_MODEL_GUITAR_SUMMIT}>
              <RockModeForGuitarSummit />
            </StepFrame>
          </Step>

          <Step
            id={STEPS.CHOOSE_INSTRUMENT_MODEL}
            when={() => jobType === JobType.TranscribeStudio && location !== 'guitar-summit'}>
            <StepFrame
              showCloseDialog={showTranscriptionDialogClose}
              setShowCloseDialog={setShowTranscriptionDialogClose}
              submitting={submitting}
              handleClose={handleCloseDialog}
              id={STEPS.CHOOSE_INSTRUMENT_MODEL}>
              <InstrumentsSelection isRetranscribe={isRetranscribe} />
            </StepFrame>
          </Step>

          <Step
            id={STEPS.CHOOSE_INSTRUMENT}
            when={() => jobType !== JobType.TranscribeStudio && location !== 'guitar-summit'}>
            <StepFrame
              showCloseDialog={showTranscriptionDialogClose}
              setShowCloseDialog={setShowTranscriptionDialogClose}
              submitting={submitting}
              handleClose={handleCloseDialog}
              id={STEPS.CHOOSE_INSTRUMENT}>
              <InstrumentsSelection isRetranscribe={isRetranscribe} />
            </StepFrame>
          </Step>
          <Step
            id={STEPS.SOLO}
            when={() =>
              (oneInstrumentSelected(settings) || noInstrumentSelected(settings)) &&
              !isPiano(settings) &&
              location !== 'guitar-summit'
            }>
            <StepFrame
              showCloseDialog={showTranscriptionDialogClose}
              setShowCloseDialog={setShowTranscriptionDialogClose}
              submitting={submitting}
              handleClose={handleCloseDialog}
              backClickSettings={{
                solo: false,
              }}
              id={STEPS.SOLO}>
              <SoloComponent />
            </StepFrame>
          </Step>
          <Step
            id={STEPS.SONG_CRITERIA}
            when={() => oneInstrumentSelected(settings) && isPiano(settings)}>
            <StepFrame
              showCloseDialog={showTranscriptionDialogClose}
              setShowCloseDialog={setShowTranscriptionDialogClose}
              submitting={submitting}
              handleClose={handleCloseDialog}
              backClickSettings={{
                solo: undefined,
                arrangement: undefined,
                instruments: [],
              }}
              id={STEPS.SONG_CRITERIA}>
              <TranscriptionMode />
            </StepFrame>
          </Step>
          <Step id={STEPS.DIFFICULTY} when={() => isPiano(settings) && isArrangement(settings)}>
            <StepFrame
              showCloseDialog={showTranscriptionDialogClose}
              setShowCloseDialog={setShowTranscriptionDialogClose}
              submitting={submitting}
              handleClose={handleCloseDialog}
              backClickSettings={{
                difficulty: undefined,
              }}
              id={STEPS.DIFFICULTY}>
              <Difficulty />
            </StepFrame>
          </Step>
          <Step id={STEPS.PEDAL} when={() => isPiano(settings) && isSolo(settings)}>
            <StepFrame
              showCloseDialog={showTranscriptionDialogClose}
              setShowCloseDialog={setShowTranscriptionDialogClose}
              submitting={submitting}
              handleClose={handleCloseDialog}
              backClickSettings={{
                piano: undefined,
              }}
              id={STEPS.PEDAL}>
              <PedalMarkings />
            </StepFrame>
          </Step>
          <Step id={STEPS.PIANO_STYLE} when={() => isPiano(settings) && !isArrangement(settings)}>
            <StepFrame
              showCloseDialog={showTranscriptionDialogClose}
              setShowCloseDialog={setShowTranscriptionDialogClose}
              submitting={submitting}
              handleClose={handleCloseDialog}
              backClickSettings={{
                piano: {
                  style: undefined,
                },
              }}
              id={STEPS.PIANO_STYLE}>
              <PianoStyle />
            </StepFrame>
          </Step>
          <Step id={STEPS.SPECIFY_INSTRUMENT} when={() => shouldSpecifyInstrument(settings)}>
            <StepFrame
              showCloseDialog={showTranscriptionDialogClose}
              setShowCloseDialog={setShowTranscriptionDialogClose}
              submitting={submitting}
              handleClose={handleCloseDialog}
              backClickSettings={{
                instruments: isWindInstrument(settings) ? ['wind'] : ['strings'],
              }}
              id={STEPS.SPECIFY_INSTRUMENT}>
              <SpecificInstrument />
            </StepFrame>
          </Step>
          <Step id={STEPS.LYRICS} when={() => hasVocals(settings)}>
            <StepFrame
              showCloseDialog={showTranscriptionDialogClose}
              setShowCloseDialog={setShowTranscriptionDialogClose}
              submitting={submitting}
              handleClose={handleCloseDialog}
              backClickSettings={{
                vocals: {
                  lyrics: undefined,
                },
              }}
              id={STEPS.LYRICS}>
              <Lyrics />
            </StepFrame>
          </Step>
          <Step
            id={STEPS.GUITAR_STYLE}
            when={() =>
              hasGuitarStyle(settings) &&
              oneInstrumentSelected(settings) &&
              settings.model !== 'rock'
            }>
            <StepFrame
              showCloseDialog={showTranscriptionDialogClose}
              setShowCloseDialog={setShowTranscriptionDialogClose}
              submitting={submitting}
              handleClose={handleCloseDialog}
              backClickSettings={{
                [settings.instruments[0]]: {
                  style: undefined,
                },
              }}
              id={STEPS.GUITAR_STYLE}>
              <GuitarStyle />
            </StepFrame>
          </Step>
          <Step
            id={STEPS.TUNING}
            when={() => hasTuning(settings) && oneInstrumentSelected(settings)}>
            <StepFrame
              showCloseDialog={showTranscriptionDialogClose}
              setShowCloseDialog={setShowTranscriptionDialogClose}
              submitting={submitting}
              handleClose={handleCloseDialog}
              backClickSettings={{
                [settings.instruments[0]]: {
                  tuning: undefined,
                },
              }}
              id={STEPS.TUNING}>
              <Tuning />
            </StepFrame>
          </Step>

          <Step id={STEPS.CAPO} when={() => hasCapo(settings) && oneInstrumentSelected(settings)}>
            <StepFrame
              showCloseDialog={showTranscriptionDialogClose}
              setShowCloseDialog={setShowTranscriptionDialogClose}
              submitting={submitting}
              handleClose={handleCloseDialog}
              backClickSettings={{
                [settings.instruments[0]]: {
                  capo: undefined,
                },
              }}
              id={STEPS.CAPO}>
              <Capo />
            </StepFrame>
          </Step>
          <Step
            id={STEPS.DRUMSET}
            when={() => isDrums(settings) && oneInstrumentSelected(settings)}>
            <StepFrame
              showCloseDialog={showTranscriptionDialogClose}
              setShowCloseDialog={setShowTranscriptionDialogClose}
              submitting={submitting}
              handleClose={handleCloseDialog}
              backClickSettings={{
                drums: {
                  drum_notes: undefined,
                },
              }}
              id={STEPS.DRUMSET}>
              <DrumSet />
            </StepFrame>
          </Step>
          <Step id={STEPS.ADVANCED} when={() => true}>
            <StepFrame
              showCloseDialog={showTranscriptionDialogClose}
              setShowCloseDialog={setShowTranscriptionDialogClose}
              submitting={submitting}
              handleClose={handleCloseDialog}
              id={STEPS.ADVANCED}>
              <AdvancedSettings isRetranscribe={isRetranscribe} />
              <ShowOnLastStep>{customSubmitButton}</ShowOnLastStep>
            </StepFrame>
          </Step>
          <Step id={STEPS.COMPOSER_TITLE} when={() => !isRetranscribe}>
            <StepFrame
              showCloseDialog={showTranscriptionDialogClose}
              setShowCloseDialog={setShowTranscriptionDialogClose}
              submitting={submitting}
              handleClose={handleCloseDialog}
              id={STEPS.COMPOSER_TITLE}>
              <ComposerTitle onSubmit={onSubmit} />
              <ShowOnLastStep>{customSubmitButton}</ShowOnLastStep>
            </StepFrame>
          </Step>
        </Wizard>
      </Paper>
    </Modal>
  );
};

export default TranscriptionDialog;
